import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContactInfo } from '@/types/assessment';
import { useAIAdvice } from '@/hooks/useAIAdvice';
import { useGlobalCalendly } from '@/contexts/CalendlyContext';
import CalendlyModal from '@/components/CalendlyModal';
import { AssessmentIntroSection } from './AssessmentIntroSection';
import { AssessmentScoreSection } from './AssessmentScoreSection';
import { AssessmentInterpretation } from './AssessmentInterpretation';
import {
  calculateAssessmentOverallScore,
  getScoreLevel,
  getTrainingScoreLevel,
} from '@/utils/assessmentUtils';
import { useQuestionnaireFromDB } from '@/hooks/useQuestionnaireFromDB';

interface AssessmentResultsProps {
  scores: Record<string, { score: number; maxScore: number }>;
  answers: any;
  contactInfo: ContactInfo;
  questions: any[];
  onClose: () => void;
  assessmentType?: 'ia-diagnostic' | 'training-transformation';
  existingAdvice?: string;
}

export const AssessmentResults: React.FC<AssessmentResultsProps> = ({
  scores,
  answers,
  contactInfo,
  assessmentType = 'ia-diagnostic',
  existingAdvice,
  onClose,
}) => {
  const safeContactInfo: ContactInfo = {
    firstName: contactInfo?.firstName || '',
    lastName: contactInfo?.lastName || '',
    email: contactInfo?.email || '',
    phone: contactInfo?.phone || '',
    company: contactInfo?.company || '',
    function: contactInfo?.function || '',
    sector: contactInfo?.sector || '',
    receiveReport: contactInfo?.receiveReport,
    orgType: contactInfo?.orgType,
    teamSize: contactInfo?.teamSize,
  };

  const { advice, isLoading: adviceLoading, error: adviceError } = useAIAdvice({
    scores,
    answers,
    contactInfo: safeContactInfo,
    assessmentType,
    existingAdvice,
  });

  const { isCalendlyOpen, closeCalendly } = useGlobalCalendly();

  const isTraining = assessmentType === 'training-transformation';
  const overallScore = calculateAssessmentOverallScore(scores, assessmentType);
  const scoreLevel = isTraining
    ? getTrainingScoreLevel(overallScore)
    : getScoreLevel(overallScore);

  const { questionnaire: trainingQ } = useQuestionnaireFromDB(
    isTraining ? 'transformation-formation-ia' : '',
  );
  const axisColors = React.useMemo(() => {
    if (!isTraining || !trainingQ) return undefined;
    const m: Record<string, string> = {};
    trainingQ.axes.forEach((a) => {
      if (a.color) m[a.name] = a.color;
    });
    return Object.keys(m).length ? m : undefined;
  }, [isTraining, trainingQ]);

  return (
    <>
      <div className="w-full max-w-full overflow-hidden px-2 md:px-4">
        <div className="space-y-6 md:space-y-8 max-w-4xl mx-auto">
          <AssessmentIntroSection
            contactInfo={safeContactInfo}
            assessmentType={assessmentType}
          />

          <AssessmentScoreSection
            scores={scores}
            overallScore={overallScore}
            scoreLevel={scoreLevel}
            axisColors={axisColors}
          />

          <div className="w-full">
            <AssessmentInterpretation
              advice={advice}
              isLoading={adviceLoading}
              error={adviceError}
            />
          </div>

          {!adviceLoading && advice && advice.trim() && isTraining && (
            <div className="rounded-2xl border border-[#D7398B]/20 bg-gradient-to-r from-[#D7398B]/[0.06] to-[#FF6600]/[0.06] p-5 md:p-6">
              <h3 className="text-lg font-extrabold text-gray-900 md:text-xl">
                Vous connaissez maintenant votre niveau de maturité IA et les principaux axes à travailler.
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600 md:text-base">
                L’Audit Flash vous permet d’aller plus loin en transformant ce diagnostic en priorités concrètes, cas d’usage à fort potentiel et premières actions à engager.
              </p>
              <Link
                to="/audit-flash-ia-formation"
                className="nokodia-link-cta mt-3 inline-flex text-sm md:text-base"
              >
                Faire mon Audit Flash
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </div>
          )}

          {!adviceLoading && advice && advice.trim() && (
            <div className="flex justify-center pt-1">
              <Button variant="outline" onClick={onClose} className="rounded-full text-sm md:text-base">
                Fermer
              </Button>
            </div>
          )}
        </div>
      </div>

      <CalendlyModal
        isOpen={isCalendlyOpen}
        onClose={closeCalendly}
      />
    </>
  );
};
